import { AccountType, type AccountType as AccountTypeT } from './account.js';

// ─── Subscription plan audience (inquirer vs supplier catalog) ─────────────

export const SubscriptionPlanAudience = {
  Inquirer: 'inquirer',
  Supplier: 'supplier',
} as const;

export const SubscriptionPlanAudienceValues = Object.values(SubscriptionPlanAudience);
export type SubscriptionPlanAudience =
  (typeof SubscriptionPlanAudience)[keyof typeof SubscriptionPlanAudience];

/** Maps account type to which subscription catalog applies (consultant + solutionProvider → supplier). */
export function subscriptionPlanAudienceForAccountType(
  accountType: AccountTypeT,
): SubscriptionPlanAudience {
  return accountType === AccountType.Inquirer
    ? SubscriptionPlanAudience.Inquirer
    : SubscriptionPlanAudience.Supplier;
}

// ─── Subscription Plan Types ────────────────────────────────────────────────

export const SubscriptionPlanType = {
  FREE: 'free',
  ENTERPRISE: 'enterprise',
  STANDARD: 'standard',
} as const;

export const SubscriptionPlanTypeValues = Object.values(SubscriptionPlanType);
export type SubscriptionPlanType = (typeof SubscriptionPlanType)[keyof typeof SubscriptionPlanType];

// ─── Subscription Status ────────────────────────────────────────────────────

export const SubscriptionStatus = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
  TRIALING: 'trialing',
  UNPAID: 'unpaid',
  PAUSED: 'paused',
  NONE: 'none',
} as const;

export const SubscriptionStatusValues = Object.values(SubscriptionStatus);
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

// ─── Price DTO ──────────────────────────────────────────────────────────────

export interface SubscriptionPriceDto {
  id: string;
  currency: string;
  /** Amount in the smallest currency unit (e.g. cents / haléře), as returned by Stripe. */
  unitAmount: number;
  interval: string;
  intervalCount: number;
}

// ─── Subscription Plan DTOs ─────────────────────────────────────────────────

export interface SubscriptionPlanDto {
  id: string;
  name: string;
  slug: string;
  type: SubscriptionPlanType;
  audience: SubscriptionPlanAudience;
  description: string | null;
  features: string[];
  stripeProductId: string | null;
  prices: SubscriptionPriceDto[];
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  /** Lifetime inquiry cap (soft-deleted included); null = unlimited. */
  maxInquiries: number | null;
  /** Lifetime offer cap; null = unlimited. */
  maxOffers: number | null;
  /** Monthly LLM spend budget in USD cents; null = unlimited. */
  monthlyLlmBudgetCents: number | null;
}

export interface CreateSubscriptionPlanDto {
  name?: string;
  slug: string;
  type: SubscriptionPlanType;
  audience: SubscriptionPlanAudience;
  description?: string | null;
  features?: string[];
  stripeProductId?: string | null;
  prices?: SubscriptionPriceDto[];
  isDefault?: boolean;
  sortOrder?: number;
  maxInquiries?: number | null;
  maxOffers?: number | null;
  monthlyLlmBudgetCents?: number | null;
}

export interface UpdateSubscriptionPlanDto {
  name?: string;
  slug?: string;
  type?: SubscriptionPlanType;
  audience?: SubscriptionPlanAudience;
  description?: string | null;
  features?: string[];
  stripeProductId?: string | null;
  prices?: SubscriptionPriceDto[];
  isDefault?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  maxInquiries?: number | null;
  maxOffers?: number | null;
  monthlyLlmBudgetCents?: number | null;
}

export interface AssignPlanDto {
  priceId?: string;
}

/** Admin: assign a subscription plan to an account from account detail. */
export interface AdminAssignAccountSubscriptionDto {
  planId: string;
  priceId?: string;
}

/** Admin: schedule Stripe subscription cancellation at period end. */
export interface AdminUpdateAccountSubscriptionDto {
  cancelAtPeriodEnd: true;
}

// ─── Account Subscription DTOs ──────────────────────────────────────────────

export interface AccountSubscriptionDto {
  id: string;
  accountId: string;
  status: SubscriptionStatus;
  planId: string | null;
  planName: string | null;
  planType: SubscriptionPlanType | null;
  stripeSubscriptionId: string | null;
  productId: string | null;
  productName: string | null;
  priceId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface CurrentSubscriptionDto {
  plan: SubscriptionPlanDto | null;
  billing: AccountSubscriptionDto | null;
}

// ─── Checkout & Portal DTOs ─────────────────────────────────────────────────

// ─── Usage / Budget DTOs ────────────────────────────────────────────────────

export const PlanLimitKind = {
  Inquiries: 'inquiries',
  Offers: 'offers',
  LlmBudget: 'llmBudget',
} as const;
export type PlanLimitKind = (typeof PlanLimitKind)[keyof typeof PlanLimitKind];

/** Current-month LLM budget status for the account (null budget = unlimited). */
export interface SubscriptionUsageDto {
  budgetCents: number | null;
  spentCents: number;
}

export interface CreateCheckoutSessionDto {
  planId: string;
  priceId: string;
}

export interface CheckoutSessionResponseDto {
  url: string;
}

export interface PortalSessionResponseDto {
  url: string;
}
